/**
 * DixonColesPage - Main Dixon-Coles data page component
 *
 * Displays upcoming fixtures with D&C model probabilities, odds, and value edges.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useDixonColesFixtures, useDCBookmakers, useDCSavedFilters, saveDCFilters, isSwrAuthReady } from '../../swr-config';
import { DixonColesTable } from './DixonColesTable';
import { DixonColesFilters } from './DixonColesFilters';
import { DixonColesMethodology } from './DixonColesMethodology';
import { DixonColesMobileCard } from './DixonColesMobileCard';
import { LoadingState } from '../LoadingState';
import { ErrorState } from '../ErrorState';
import { useFeatureTracking, FeatureNames } from '../../hooks/useActivityTracking';
import type { DCFilters, DCSortColumn, DixonColesFixture } from '../../types';

const DEFAULT_FILTERS: DCFilters = {
  days_ahead: 7,
  bookmaker_slug: null,
  sort_by: 'kickoff',
  sort_order: 'asc',
};

// Skeleton component for loading state
function TableSkeleton() {
  return (
    <div className="dc-table-skeleton">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="dc-skeleton-row">
          <div className="skeleton skeleton-text" style={{ width: '80px' }} />
          <div className="skeleton skeleton-text" style={{ width: '200px' }} />
          <div className="skeleton skeleton-text" style={{ width: '100px' }} />
          <div className="skeleton skeleton-text" style={{ width: '60px' }} />
          <div className="skeleton skeleton-text" style={{ width: '60px' }} />
          <div className="skeleton skeleton-text" style={{ width: '60px' }} />
        </div>
      ))}
    </div>
  );
}

export function DixonColesPage() {
  // Track feature usage
  useFeatureTracking(FeatureNames.DIXON_COLES);

  // Auth state for preset fetching
  const { isSignedIn, isLoaded: authLoaded } = useAuth();

  // Track when SWR auth token getter is configured
  const [swrAuthReady, setSwrAuthReady] = useState(isSwrAuthReady());

  useEffect(() => {
    if (swrAuthReady) return;

    const checkAuth = () => {
      if (isSwrAuthReady()) {
        setSwrAuthReady(true);
      }
    };

    checkAuth();
    const interval = setInterval(checkAuth, 50);
    const timeout = setTimeout(() => clearInterval(interval), 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [swrAuthReady]);

  // Only fetch presets when user is authenticated AND SWR auth token getter is configured
  const shouldFetchPresets = authLoaded && isSignedIn && swrAuthReady;

  const [filters, setFilters] = useState<DCFilters>(DEFAULT_FILTERS);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileView, setMobileView] = useState<'cards' | 'table'>('table');
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch data
  const { data, error, isLoading, mutate } = useDixonColesFixtures(filters);
  const { data: bookmakersData } = useDCBookmakers();
  const { data: savedFilters, isLoading: savedFiltersLoading } = useDCSavedFilters();

  // Load saved filters on mount (only once)
  useEffect(() => {
    if (!savedFiltersLoading && !filtersLoaded) {
      if (savedFilters) {
        setFilters(prev => ({ ...prev, ...savedFilters }));
      }
      setFiltersLoaded(true);
    }
  }, [savedFilters, savedFiltersLoading, filtersLoaded]);

  // Save filters when they change (debounced)
  useEffect(() => {
    if (!filtersLoaded) return; // Don't save until initial load complete

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save by 1 second
    saveTimeoutRef.current = setTimeout(() => {
      saveDCFilters(filters).catch(err => {
        console.warn('Failed to save DC filters:', err);
      });
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [filters, filtersLoaded]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSort = useCallback((column: DCSortColumn) => {
    setFilters(prev => ({
      ...prev,
      sort_by: column,
      sort_order: prev.sort_by === column && prev.sort_order === 'asc' ? 'desc' as const : 'asc' as const,
    }));
  }, []);

  const handleToggleExpand = useCallback((fixtureId: number) => {
    setExpandedId(prev => prev === fixtureId ? null : fixtureId);
  }, []);

  const handleFilterChange = useCallback((newFilters: Partial<DCFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const handleLoadPreset = useCallback((presetFilters: DCFilters) => {
    setFilters(presetFilters);
  }, []);

  if (error) {
    return (
      <div className="dc-page">
        <ErrorState
          message="Failed to load Dixon-Coles data"
          onRetry={() => mutate()}
        />
      </div>
    );
  }

  return (
    <div className="dc-page">
      <header className="dc-header">
        <div className="dc-header-left">
          <h1>Dixon-Coles</h1>
          <span className="dc-subtitle">Model-based probabilities</span>
        </div>
      </header>

      <DixonColesMethodology />

      <DixonColesFilters
        filters={filters}
        onChange={handleFilterChange}
        onLoadPreset={handleLoadPreset}
        onClearFilters={handleClearFilters}
        bookmakers={bookmakersData?.bookmakers || []}
        userPreferredSlug={bookmakersData?.user_preferred_slug}
        shouldFetchPresets={shouldFetchPresets}
      />

      <LoadingState
        isLoading={isLoading}
        skeleton={<TableSkeleton />}
      >
        {!data || data.fixtures.length === 0 ? (
          <div className="dc-empty">
            <p>No fixtures found matching your filters.</p>
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="dc-count-row">
              <div className="dc-count">
                {data.total_count} fixtures &bull; Odds: {data.bookmaker_used}
              </div>

              {/* Mobile view toggle */}
              {isMobile && (
                <div className="dc-view-toggle">
                  <button
                    className={`view-toggle-btn ${mobileView === 'cards' ? 'active' : ''}`}
                    onClick={() => setMobileView('cards')}
                    type="button"
                    title="Card view"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="1" y="1" width="6" height="6" rx="1" />
                      <rect x="9" y="1" width="6" height="6" rx="1" />
                      <rect x="1" y="9" width="6" height="6" rx="1" />
                      <rect x="9" y="9" width="6" height="6" rx="1" />
                    </svg>
                  </button>
                  <button
                    className={`view-toggle-btn ${mobileView === 'table' ? 'active' : ''}`}
                    onClick={() => setMobileView('table')}
                    type="button"
                    title="Table view"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="1" y="2" width="14" height="2" rx="0.5" />
                      <rect x="1" y="7" width="14" height="2" rx="0.5" />
                      <rect x="1" y="12" width="14" height="2" rx="0.5" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {isMobile && mobileView === 'cards' ? (
              <div className="dc-mobile-list">
                {data.fixtures.map((fixture: DixonColesFixture) => (
                  <DixonColesMobileCard
                    key={fixture.fixture_id}
                    fixture={fixture}
                    isExpanded={expandedId === fixture.fixture_id}
                    onToggle={() => handleToggleExpand(fixture.fixture_id)}
                  />
                ))}
              </div>
            ) : (
              <DixonColesTable
                fixtures={data.fixtures}
                sortColumn={(filters.sort_by || 'kickoff') as DCSortColumn}
                sortOrder={filters.sort_order || 'asc'}
                onSort={handleSort}
                expandedId={expandedId}
                onToggleExpand={handleToggleExpand}
              />
            )}
          </>
        )}
      </LoadingState>
    </div>
  );
}
